import { Button, Grid, GridItem, Input, Text } from "@innovationnorway/lighthouse-components";
import React, { useState } from "react";
import useSWR from "swr";
import { ErrorCodes } from "../../../main/models/error-codes";
import { ResendVerificationEmailRequest } from "../../../main/models/resend-verification-email-request";
import { ResendVerificationEmailResponse } from "../../../main/models/resend-verification-email-response";
import { VerifyEmailRequest } from "../../../main/models/verify-email-request";
import { GetValidatedObject } from "../../../utils/validationUtils";
import { getUserAccounts } from "../../apis/getUserAccounts";
import { resendVerification } from "../../apis/resendVerification";
import { verifyEmail } from "../../apis/verifyEmail";
import { useErrors } from "../../hooks/useErrors";
import { useLayout } from "../../hooks/useLayout";

interface VerifyEmailConfirmationCodeProps {
  accountPublicId?: string;
  recipientEmail?: string;
  onSuccess: (emailIsVerified: boolean) => void;
  onCancel?: () => void;
}

const VerifyEmailConfirmationCode = ({
  accountPublicId,
  recipientEmail,
  onSuccess,
  onCancel
}: VerifyEmailConfirmationCodeProps) => {
  const { translations } = useLayout();
  const errorCodes = useErrors();

  const { mutate } = useSWR(`userAccounts`, getUserAccounts);

  const [code, setCode] = useState<string>("");
  const [verificationObject, setValidationObject] = useState<VerifyEmailRequest>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [verificationResent, setVerificationResent] = useState<boolean>(false);
  const [generalError, setGeneralError] = useState<string>();
  const [email, setEmail] = useState<string>(recipientEmail || "");

  const submit = async () => {
    setIsLoading(true);
    setValidationObject(undefined);
    const verificationResult = await verifyEmail({
      code: code,
      accountPublicId: accountPublicId
    } as VerifyEmailRequest);

    if (verificationResult.code === 200) {
      mutate();
      onSuccess(true);
    } else if (verificationResult.code === 400) {
      const validationObject = GetValidatedObject<VerifyEmailRequest>(verificationResult);
      setValidationObject(validationObject);
    } else if (verificationResult.code === 404) {
      setGeneralError(translations.verifyEmailNoVerificationExists);
    } else {
      setVerificationResent(false);
      setGeneralError(translations.internalServerError);
    }
    setIsLoading(false);
  };

  const resend = async () => {
    setIsLoading(true);
    const res = await resendVerification({
      accountPublicId: accountPublicId
    } as ResendVerificationEmailRequest);

    if (res.code === 200) {
      const resendResponse = res.body as ResendVerificationEmailResponse;
      setVerificationResent(true);
      setEmail(resendResponse.recipientEmail);
    } else if (res.code === 404) {
      setGeneralError(translations.verifyEmailNoVerificationExists);
    } else {
      setVerificationResent(false);
      setGeneralError(translations.internalServerError);
    }

    setIsLoading(false);
  };

  return (
    <Grid>
      {email && (
        <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
          <Text as="p">
            {translations.verifyEmailSentVerificationText} {email}
          </Text>
          <Text as="p">{translations.verifyEmailInstructionsText}</Text>
        </GridItem>
      )}
      <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
        <Grid>
          <GridItem spanLarge={4} spanMedium={4} spanSmall={4}>
            <Text as="label">
              <strong>{translations.verifyEmailCodeLabel}</strong>
              <Input
                placeholder={translations.verifyEmailConfirmationCode}
                type="text"
                onChange={(e) => setCode(e.target.value)}
                value={code}
                errorMessage={errorCodes[verificationObject?.code as keyof ErrorCodes] ?? generalError}
              />
            </Text>
          </GridItem>
        </Grid>
      </GridItem>
      <GridItem spanLarge={8} spanMedium={8} spanSmall={4}>
        {!verificationResent && !generalError && !isLoading && (
          <Text>
            {translations.verifyEmailNoCodeLabel}{" "}
            <Text className="inline_clickable_text" onClick={resend}>
              {translations.verifyEmailResendCode}
            </Text>
          </Text>
        )}
      </GridItem>
      <GridItem spanLarge={8} spanMedium={8} spanSmall={4}>
        <Grid>
          <GridItem spanLarge={3} spanMedium={3} spanSmall={1}>
            <Button variant="primary" isLoading={isLoading} disabled={isLoading || !code} onClick={submit}>
              {translations.verifyEmailCheckCodeLabel}
            </Button>
          </GridItem>
          <GridItem spanLarge={3} spanMedium={3} spanSmall={1}>
            <Button variant="secondary" disabled={isLoading} onClick={onCancel}>
              {translations.backBtnLabel}
            </Button>
          </GridItem>
        </Grid>
      </GridItem>
    </Grid>
  );
};

export default VerifyEmailConfirmationCode;
